import {
  createContext,
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  FetchResult,
  MutationResult,
  ApolloError,
} from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { isNotNullish } from '@app/helpers/predicate';

import useAlerts, { AlertItem } from '../hooks/useAlerts';
import { AlertTitle, AlertType } from '../components/Alert';
import {
  CART_QUERY,
  ADD_PRODUCTS_TO_CART_MUTATION,
  UPDATE_CART_ITEMS_MUTATION,
  REMOVE_CART_ITEM_MUTATION,
  APPLY_COUPON_MUTATION,
  REMOVE_COUPON_MUTATION,
} from './CartContext.queries';
import messages from './CartContext.messages';

import {
  CartQuery,
  CartQueryVariables,
  CartQuery_magento2,
  CartQuery_magento2_cart,
  CartQuery_magento2_cart_appliedCoupons,
} from './__generated__/CartQuery';
import {
  UpdateCartItemsMutation,
  UpdateCartItemsMutationVariables,
} from './__generated__/UpdateCartItemsMutation';
import {
  RemoveCartItemMutation,
  RemoveCartItemMutationVariables,
} from './__generated__/RemoveCartItemMutation';
import {
  ApplyCouponMutation,
  ApplyCouponMutationVariables,
} from './__generated__/ApplyCouponMutation';
import {
  RemoveCouponMutation,
  RemoveCouponMutationVariables,
} from './__generated__/RemoveCouponMutation';
import useCartId from '../hooks/useCartId';
import {
  AddProductsToCartMutation,
  AddProductsToCartMutationVariables,
  AddProductsToCartMutation_magento2_addProductsToCart_userErrors,
} from './__generated__/AddProductsToCartMutation';

export interface CartContextValue {
  loading: boolean;
  errors: ApolloError[];
  userErrors: AddProductsToCartMutation_magento2_addProductsToCart_userErrors[];
  cartId?: string;
  cartUserType?: string;
  items?: CartQuery_magento2_cart['items'];
  prices?: CartQuery_magento2_cart['prices'];
  storeConfig?: CartQuery_magento2['storeConfig'];
  alerts: AlertItem[];
  isVirtual?: boolean;
  setItemQuantity: (id: string, quantity: number) => Promise<void>;
  loadingCoupon: boolean;
  applyCoupon: (
    code: CartQuery_magento2_cart_appliedCoupons['code']
  ) => Promise<FetchResult<ApplyCouponMutation> | void>;
  coupons?: CartQuery_magento2_cart['appliedCoupons'];
  removeCoupon: () => Promise<FetchResult<RemoveCouponMutation> | void>;
  removeItem: (id: string) => Promise<void>;
  addItems: (items: { sku: string; quantity: number }[]) => Promise<void>;
  addProductsResult: MutationResult<AddProductsToCartMutation> | null;
  termsAccepted: boolean;
  setTermsAccepted: (isAccepted: boolean) => void;
}

const initialValue: CartContextValue = {
  loading: true,
  errors: [],
  userErrors: [],
  coupons: [],
  applyCoupon: Promise.resolve,
  removeCoupon: Promise.resolve,
  loadingCoupon: true,
  alerts: [],
  setItemQuantity: Promise.resolve,
  addItems: Promise.resolve,
  removeItem: Promise.resolve,
  addProductsResult: null,
  termsAccepted: false,
  setTermsAccepted: () => undefined,
};

export const CartContext = createContext<CartContextValue>(initialValue);

const TERMS_AND_CONDITIONS_STATUS_KEY = 'nicom/shop/cart-terms-conditions';

export const CartProvider: FunctionComponent = ({ children }) => {
  const { cartId, cartUserType, ...cartIdQueryState } = useCartId();
  const { alerts, clearAlerts, addAlerts } = useAlerts([]);
  const [termsAccepted, setTermsAcceptedState] = useState<boolean>(
    !!localStorage.getItem(TERMS_AND_CONDITIONS_STATUS_KEY)
  );

  const setTermsAccepted = (isAccepted: boolean) => {
    if (isAccepted) {
      localStorage.setItem(
        TERMS_AND_CONDITIONS_STATUS_KEY,
        JSON.stringify(true)
      );
    } else {
      localStorage.removeItem(TERMS_AND_CONDITIONS_STATUS_KEY);
    }

    setTermsAcceptedState(isAccepted);
  };

  const cartResult = useQuery<CartQuery, CartQueryVariables>(CART_QUERY, {
    skip: !cartId,
    variables: { cartId: cartId || '' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    ssr: false,
    onCompleted: (data) => {
      if (data?.magento2?.cart) {
        const items = data?.magento2?.cart.items?.filter(isNotNullish) ?? null;
        const localItems = localStorage.getItem('localItemsCart');

        let localItemsCart: string[] = [];

        if (localItems) {
          const localItemsArray: string[] = JSON.parse(localItems);

          localItemsCart = localItemsArray;
        }

        if (items) {
          items.forEach((item) => {
            if (!item.product?.uid) return;

            if (!localItemsCart.includes(item.product.uid)) {
              localItemsCart.push(item.product.uid);
              addAlerts([
                {
                  type: AlertType.Success,
                  content: (
                    <AlertTitle>
                      {item?.product.name}{' '}
                      <FormattedMessage {...messages.newItemAdded} />
                    </AlertTitle>
                  ),
                },
              ]);
              localStorage.setItem(
                'localItemsCart',
                JSON.stringify(localItemsCart)
              );
            }
          });
        }
      }
    },
    onError: (error) => {
      clearAlerts();
      addAlerts([
        {
          type: AlertType.Error,
          content: <AlertTitle>{error.message}</AlertTitle>,
        },
      ]);
    },
  });

  const [addProductsToCart, addProductsResult] = useMutation<
    AddProductsToCartMutation,
    AddProductsToCartMutationVariables
  >(ADD_PRODUCTS_TO_CART_MUTATION);

  const [removeCoupon, removeCouponResult] = useMutation<
    RemoveCouponMutation,
    RemoveCouponMutationVariables
  >(REMOVE_COUPON_MUTATION, {
    variables: { cartId: cartId || '' },
    onCompleted: clearAlerts,
  });

  const [applyCoupon, couponResult] = useMutation<
    ApplyCouponMutation,
    ApplyCouponMutationVariables
  >(APPLY_COUPON_MUTATION, {
    onError: (error) => {
      clearAlerts();
      addAlerts([
        {
          type: AlertType.Error,
          content: <AlertTitle>{error.message}</AlertTitle>,
          shouldShake: true,
        },
      ]);
    },
    onCompleted: () => {
      clearAlerts();
      addAlerts([
        {
          type: AlertType.Success,
          content: (
            <AlertTitle>
              <FormattedMessage {...messages.couponUpdated} />
            </AlertTitle>
          ),
        },
      ]);
    },
  });

  const [updateCartItems, updateResult] = useMutation<
    UpdateCartItemsMutation,
    UpdateCartItemsMutationVariables
  >(UPDATE_CART_ITEMS_MUTATION);

  const [removeCartItem, removeResult] = useMutation<
    RemoveCartItemMutation,
    RemoveCartItemMutationVariables
  >(REMOVE_CART_ITEM_MUTATION);

  const cart = cartResult.data?.magento2?.cart;
  const storeConfig = cartResult?.data?.magento2?.storeConfig;

  const couponContext = {
    coupons: cart?.appliedCoupons,
    removeCoupon,
    applyCoupon: (code: CartQuery_magento2_cart_appliedCoupons['code']) =>
      applyCoupon({ variables: { cartId: cartId || '', couponCode: code } }),
    loadingCoupon: couponResult.loading || removeCouponResult.loading,
  };

  const addItems = useCallback(
    async (items) => {
      if (!cartId) return;

      addProductsToCart({ variables: { cartId, cartItems: items } });
    },
    [cartId, addProductsToCart]
  );

  const setItemQuantity = useCallback<CartContextValue['setItemQuantity']>(
    async (id, quantity) => {
      if (!cartId) return;

      await updateCartItems({
        variables: {
          cartId,
          cartItems: [{ quantity, cart_item_uid: id }],
        },
      });
    },
    [cartId, updateCartItems]
  );

  const removeItem = useCallback<CartContextValue['removeItem']>(
    async (id) => {
      if (!cartId) return;

      await removeCartItem({
        variables: {
          cartId,
          cartItemId: id,
        },
      });
      const localItems = localStorage.getItem('localItemsCart');
      if (localItems) {
        const localItemsArray: string[] = JSON.parse(localItems);
        localItemsArray.splice(localItemsArray.indexOf(id), 1);
        localStorage.setItem('localItemsCart', JSON.stringify(localItemsArray));
      }
      clearAlerts();
    },
    [cartId, removeCartItem, clearAlerts]
  );

  const {
    error: cartIdQueryError,
    loading: cartIdQueryLoading,
  } = cartIdQueryState;
  const cartIdError = useMemo(
    () => (!cartIdQueryLoading && !cartId ? cartIdQueryError : null),
    [cartId, cartIdQueryError, cartIdQueryLoading]
  );

  return (
    <CartContext.Provider
      value={{
        setItemQuantity,
        removeItem,
        addItems,
        addProductsResult,
        cartId,
        isVirtual: cart?.isVirtual,
        cartUserType,
        alerts,
        termsAccepted,
        setTermsAccepted,
        storeConfig: storeConfig,
        items: cart?.items,
        prices: cart?.prices,
        loading:
          cartIdQueryState.loading ||
          cartResult.loading ||
          addProductsResult.loading ||
          updateResult.loading ||
          removeResult.loading,
        ...couponContext,
        errors: [
          cartIdError,
          cartResult.error,
          updateResult.error,
          removeResult.error,
          addProductsResult.error,
        ].filter(isNotNullish),
        userErrors:
          (addProductsResult.data?.magento2?.addProductsToCart?.userErrors?.filter(
            Boolean
          ) as AddProductsToCartMutation_magento2_addProductsToCart_userErrors[]) ??
          [],
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
